import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
    FaSpinner, FaHome, FaPlus, FaSearch, FaEdit,
    FaEye, FaTrash, FaHandHoldingUsd, FaMoneyBillWave, FaPercentage
} from 'react-icons/fa';

import DevedorModal from './DevedorModal.js'; // Novo Modal para CRUD de Devedores
import { ui } from '../utils/styles'; // Assumindo que os estilos 'ui' foram movidos para um arquivo utilitário.
// Se não, copie a constante 'ui' para este novo arquivo ou importe-a.

// Adaptando o helper makePageList, que deve estar em utils/helpers.js ou no final do arquivo.
function makePageList(total, current, delta = 2) {
    // ... (mesma função de paginação do ClienteControlScreen)
    if (total <= 1) return [1];
    const range = [1];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    if (left > 2) range.push('…');
    for (let i = left; i <= right; i++) range.push(i);
    if (right < total - 1) range.push('…');
    range.push(total);
    return range;
}

const DevedorControlScreen = () => {
    const [nomeFilter, setNomeFilter] = useState('');
    const [cpfCnpjFilter, setCpfCnpjFilter] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('Ativo');

    const [allDevedores, setAllDevedores] = useState([]); // Novo estado
    const [currentDevedores, setCurrentDevedores] = useState([]); // Novo estado

    const [currentPage, setCurrentPage] = useState(1);
    const [devedoresPerPage, setDevedoresPerPage] = useState(12); // Alterado de clientesPerPage

    const totalPages = useMemo(
        () => Math.ceil(allDevedores.length / devedoresPerPage) || 0,
        [allDevedores.length, devedoresPerPage]
    );

    const pageList = useMemo(
        () => makePageList(totalPages, currentPage, 2),
        [totalPages, currentPage]
    );

    const [loading, setLoading] = useState(false);
    const [loadingClientes, setLoadingClientes] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedDevedor, setSelectedDevedor] = useState(null);
    const [listaClientes, setListaClientes] = useState([]); // Para alimentar o modal
    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const navigate = useNavigate();

    // token
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    // Emitente
    const emitente = useMemo(
        () => localStorage.getItem('userIdEmitente'),
        []
    );

    useEffect(() => {
        fetchDevedores();
        fetchClientes(); // Aproveita e carrega os clientes para os modais
    }, [selectedStatus]);

    // Função para buscar clientes (necessária para o modal de devedor/divida)
    const fetchClientes = async () => {
        try {
            const { data } = await axios.get(`${URL_BACKEND}/clientes/Ativo/${emitente}`, { headers: authHeader });
            setListaClientes(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Erro ao buscar clientes para o modal", e);
        }
    };

    const fetchDevedores = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${URL_BACKEND}/devedores/${selectedStatus}/?nome=${encodeURIComponent(nomeFilter || '')}&cpfCnpj=${encodeURIComponent(cpfCnpjFilter || '')}`,
                { headers: authHeader }
            );
            setAllDevedores(Array.isArray(data) ? data : []);
            setCurrentPage(1);
        } catch (error) {
            console.log(JSON.stringify(error));

            toast.error('Erro ao carregar devedores.' + error);
        } finally {
            setLoading(false);
        }
    };

    // Paginação local
    useEffect(() => {
        const indexOfLast = currentPage * devedoresPerPage;
        const indexOfFirst = indexOfLast - devedoresPerPage;
        setCurrentDevedores(allDevedores.slice(indexOfFirst, indexOfLast));
    }, [allDevedores, currentPage, devedoresPerPage]);

    // Funções CRUD e Navegação
    const goToPage = (page) => setCurrentPage(page);

    const handleCreate = () => { setModalMode('create'); setSelectedDevedor(null); setShowModal(true); };
    const handleEdit = (devedor) => { setModalMode('edit'); setSelectedDevedor(devedor); setShowModal(true); };
    const handleView = (devedor) => { setModalMode('view'); setSelectedDevedor(devedor); setShowModal(true); };

    // NOVO: Navegar para a tela de Dívidas (Detalhe do Devedor)
    const handleViewDividas = (devedor) => {
        navigate(`/devedores/${devedor.id}/dividas`, { state: { devedor, listaClientes } });
    };

    // NOVO: Navegar para a tela de Dívidas (Detalhe do Devedor)
    const handleViewTodasDividas = (devedor) => {
        navigate(`/devedores/dividas/todas`);
    };


    // Reutilizando formatCpfCnpj
    function formatCpfCnpj(value) {
        if (!value) return '';
        const digits = value.replace(/\D/g, '');
        if (digits.length <= 11) {
            return digits
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            return digits
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/\.(\d{3})(\d)/, '.$1/$2')
                .replace(/(\d{4})(\d)/, '$1-$2');
        }
    }

    return (
        <div style={ui.container}>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 20px',
                borderRadius: '12px',
                background: 'linear-gradient(to right, #667eea, #764ba2)',
                color: '#fff',
                marginBottom: '24px'
            }}>
                <h1 style={{ fontSize: '1.2rem', fontWeight: '600' }}>Gestão de Devedores</h1>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.5)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                    }}
                >
                    <FaHome /> Voltar
                </button>
            </div>


            {/* FILTROS */}
            <div style={ui.filters}>
                {/* Filtros de Nome e CPF/CNPJ (mantidos) */}
                <div style={ui.filterGroup}> {/* ... Nome */} </div>
                <div style={ui.filterGroup}> {/* ... CPF/CNPJ */} </div>
                <div style={ui.filterGroup}>
                    <label style={ui.label}>Status</label>
                    <select
                        style={ui.select}
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(String(e.target.value))}
                    >
                        <option value="Todos">Todos</option>
                        <option value="Ativo">Ativos</option>
                        <option value="Inativo">Inativos</option>
                    </select>
                </div>

                <div style={{ ...ui.actionsRow, alignItems: 'flex-end', paddingTop: 4 }}>
                    <button style={{ ...ui.buttonPrimary, background: '#0e7490' }} onClick={fetchDevedores} disabled={loading}>
                        {loading ? <FaSpinner className="fa-spin" /> : <><FaSearch /> Buscar</>}
                    </button>
                    <button style={{ ...ui.buttonGreen, background: '#047857' }} onClick={handleCreate}>
                        <FaPlus /> Novo Devedor
                    </button>
                    <button title="Ver Dívidas"
                        style={{
                            background: '#3b82f6',
                            border: 'none',
                            color: '#fff',
                            borderRadius: 6,
                            cursor: 'pointer',
                            marginLeft: 10,
                            // fontSize: '1.2rem',
                            padding: '8px 30px'
                        }}
                        onClick={() => handleViewTodasDividas()}>
                        <FaMoneyBillWave /> Dividas
                    </button>
                </div>

                <div style={{ ...ui.actionsRow, alignItems: 'flex-end', paddingTop: 4 }}>
                    <button
                        title="Ver Comissões"
                        style={{
                            background: '#8b5cf6', // Um roxo bonito para diferenciar das dívidas
                            border: 'none',
                            color: '#fff',
                            borderRadius: 6,
                            cursor: 'pointer',
                            marginLeft: 10,
                            padding: '8px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        onClick={() => navigate('/comissoes')}
                    >
                        <FaPercentage /> Comissões
                    </button>
                </div>

            </div>

            {/* TABELA DE DEVEDORES */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p style={{ padding: 10 }}>Carregando...</p>
                ) : currentDevedores.length === 0 ? (
                    <p style={{ color: 'red', padding: 16 }}>Nenhum devedor encontrado.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    <th style={ui.th}>CNPJ/CPF</th>
                                    <th style={ui.th}>Nome do Devedor</th>
                                    <th style={ui.th}>Fone Contato</th>
                                    <th style={ui.thRigth}>Dívida Total</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentDevedores.map((d, idx) => (
                                    <tr key={d.id} style={{ ...ui.tr, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                        <td style={ui.tdMono}>
                                            {formatCpfCnpj(d.tipo_pessoa === 'PESSOA_FISICA' ? d.cpf : d.cnpj)}
                                        </td>
                                        <td style={ui.tdClamp}>{d.nome}</td>
                                        <td style={ui.tdClamp}>{d.foneContato}</td>
                                        <td style={ui.tdCenter}>
                                            R$ {d.saldo_atual ? Number(d.saldo_atual).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0,00'}
                                        </td>
                                        <td style={ui.tdActions}>
                                            <button title="Ver Dívidas" style={{ ...ui.actionView, background: '#0e7490' }} onClick={() => handleViewDividas(d)}>
                                                <FaMoneyBillWave />
                                            </button>
                                            <button title="Visualizar" style={ui.actionView} onClick={() => handleView(d)}><FaEye /></button>
                                            <button title="Editar" style={ui.actionEdit} onClick={() => handleEdit(d)}><FaEdit /></button>
                                            <button title="Excluir" style={ui.actionDelete} onClick={() => alert('Implementar exclusão')}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                            <colgroup>
                                <col style={{ width: '130px' }} />   {/* CNPJ */}
                                <col style={{ width: '220px' }} />   {/* Devedor */}
                                <col style={{ width: '100px' }} />   {/* Valor */}
                                <col style={{ width: '100px' }} />   {/* Fone */}
                                <col style={{ width: '120px' }} />   {/* Ações */}
                            </colgroup>

                        </table>
                    </div>
                )}
            </div>

            {/* PAGINAÇÃO (mantida) */}
            {/* ... (código de paginação do ClienteControlScreen) ... */}

            {/* MODAL DEVEDOR */}
            <DevedorModal
                show={showModal}
                onClose={() => setShowModal(false)}
                devedor={selectedDevedor}
                onSave={fetchDevedores}
                mode={modalMode}
                clientesContratantes={listaClientes} // Agora passando a lista real!
            />
        </div>
    );
};


export default DevedorControlScreen;

// NOTA: É necessário criar o componente DevedorModal e a tela de Dividas (DivadasScreen)